import { useState, useEffect } from 'react'
import Header from '@components/Header/Header'
import PhoneInput from '@components/PhoneInput/PhoneInput'
import NumericKeyboard from '@components/NumericKeyboard/NumericKeyboard'
import PrimaryButton from '@components/PrimaryButton/PrimaryButton'
import AmountOption from '@components/AmountOption/AmountOption'
import RecentDestinations from '@components/RecentDestinations/RecentDestinations'
import { useRecentDestinations, toLocalPhoneNumber } from '@hooks/useRecentDestinations'
import LoadingModal from '@components/LoadingModal/LoadingModal'
import Snackbar from '@components/Snackbar/Snackbar'
import { useNavigate, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { usePhoneValidation } from '@hooks/usePhoneValidation'
import { useKeypadCaret } from '@hooks/useKeypadCaret'
import { usePpobProducts, PpobProduct } from '@hooks/usePpobProducts'
import { PpobProductCategory } from '@hooks/usePpobServices'
import { setHeaderColor } from '@lib/telegram'
import { WHITE } from '@styles/colors'
import {
  Container,
  ContentContainer,
  PhoneInputWrapper,
  KeyboardSection,
  AmountSection,
  AmountLabel,
  AmountGrid,
  ButtonWrapper
} from './PhoneCredit.styles'

const PhoneCredit = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { validatePhoneNumber, isValidating, normalizePhoneNumber } = usePhoneValidation()
  const { products, isLoading: isLoadingProducts, fetchProducts } = usePpobProducts()
  const { destinations: recentDestinations, fetchRecentDestinations } = useRecentDestinations()

  const state = location.state as { category?: PpobProductCategory } | undefined
  const category = state?.category
  const [step, setStep] = useState<'input' | 'amount'>('input')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [normalizedPhoneNumber, setNormalizedPhoneNumber] = useState('')
  const [providerName, setProviderName] = useState<string>()
  const [providerId, setProviderId] = useState<string>()
  const [providerImageUrl, setProviderImageUrl] = useState<string | null>()
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<PpobProduct | null>(null)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [showSnackbar, setShowSnackbar] = useState(false)

  useEffect(() => {
    setHeaderColor(WHITE)
  }, [])

  useEffect(() => {
    fetchRecentDestinations('MOBILE_PREPAID')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { inputRef, handleKeyboardChange } = useKeypadCaret(phoneNumber, setPhoneNumber, {
    maxLength: 15,
    stripLeadingZeros: true,
  })

  const handleContinue = async () => {
    if (step === 'input') {
      // Validate phone number first
      const validation = await validatePhoneNumber(phoneNumber, category)

      if (validation.success) {
        // Save normalized phone number for payment
        const normalized = normalizePhoneNumber(phoneNumber)
        setNormalizedPhoneNumber(normalized)

        setProviderName(validation.providerName)
        setProviderId(validation.providerId)
        setProviderImageUrl(validation.providerImageUrl)
        setStep('amount')

        // Fetch products for this provider
        if (validation.providerName) {
          await fetchProducts({
            category: category,
            brand: validation.providerName,
            size: 100,
            sortBy: 'sellingPrice'
          })
        }
      } else {
        setSnackbarMessage(t('phoneCredit.errors.invalidPhoneNumber'))
        setShowSnackbar(true)
      }
    } else {
      // Navigate to payment confirm page
      navigate('/payment-confirm', {
        state: {
          phoneNumber: normalizedPhoneNumber,
          providerName,
          providerImageUrl,
          amount: selectedProduct?.sellingPrice,
          product: selectedProduct,
          productAmount: selectedProduct?.productAmount,
          paymentType: 'prepaid',
          adminFee: selectedProduct?.adminFee,
          platformFee: selectedProduct?.platformFee
        }
      })
    }
  }

  const handleAmountSelect = (index: number) => {
    setSelectedAmount(index)
    setSelectedProduct(products[index])
  }

  const handleSnackbarClose = () => {
    setShowSnackbar(false)
  }

  const handleBack = () => {
    if (step === 'amount') {
      setStep('input')
    } else {
      navigate(-1)
    }
  }

  return (
    <Container>
      <LoadingModal visible={isValidating || isLoadingProducts} />
      <Snackbar
        message={snackbarMessage}
        isVisible={showSnackbar}
        onClose={handleSnackbarClose}
        textAlign="center"
        width="100%"
      />
      <Header
        title={t('phoneCredit.header')}
        showBackButton={true}
        onBackClick={handleBack}
      />
      <ContentContainer>
        <PhoneInputWrapper>
          <PhoneInput
            ref={inputRef}
            value={phoneNumber}
            onChange={setPhoneNumber}
            readOnly={step !== 'input'}
            iconType='credit'
            providerName={providerName}
            providerImageUrl={providerImageUrl}
          />
        </PhoneInputWrapper>

        {step === 'input' && (
          <RecentDestinations
            numbers={recentDestinations.map((d) => toLocalPhoneNumber(d.customer_no))}
            onSelect={(number) => setPhoneNumber(number.replace(/^0+/, ''))}
          />
        )}

        {step === 'input' ? (
          <KeyboardSection>
            <NumericKeyboard onChange={handleKeyboardChange} />
            <PrimaryButton
              onClick={handleContinue}
              disabled={!phoneNumber}
            >
              {t('common.continue')}
            </PrimaryButton>
          </KeyboardSection>
        ) : (
          <>
            <AmountSection>
              <AmountLabel>{t('phoneCredit.chooseAmount')}</AmountLabel>
              {products.length > 0 ? (
                <AmountGrid>
                  {products.map((product, index) => (
                    <div key={product.id} onClick={() => handleAmountSelect(index)}>
                      <AmountOption
                        idr={product.productAmount}
                        jidr={product.sellingPrice}
                        selected={selectedAmount === index}
                      />
                    </div>
                  ))}
                </AmountGrid>
              ) : (
                <AmountLabel>{t('phoneCredit.noProductsAvailable')}</AmountLabel>
              )}
            </AmountSection>
            <ButtonWrapper>
              <PrimaryButton
                onClick={handleContinue}
                disabled={selectedAmount === null}
              >
                {t('common.continue')}
              </PrimaryButton>
            </ButtonWrapper>
          </>
        )}
      </ContentContainer>
    </Container>
  )
}

export default PhoneCredit
